"use client";

import { AuthErrorDisplay } from "@packages/app-shell/AuthErrorDisplay";
import { DebugStateInfo } from "@packages/lib/auth/utils";
import * as Sentry from "@sentry/nextjs";
import React, { useEffect, useState } from "react";

/**
 * Authentication error codes for diagnostic purposes
 */
export enum AuthErrorCode {
  REDIRECT_TIMEOUT = "REDIRECT_TIMEOUT",
  REDIRECT_LOOP = "REDIRECT_LOOP",
  REDIRECT_FAILED = "REDIRECT_FAILED",
}

type BrowserDiagnosticInfo = {
  userAgent?: string;
  cookiesEnabled?: boolean;
  onLine?: boolean;
  referrer?: string;
  windowLocation?: string;
  errorCode?: AuthErrorCode;
  [key: string]: unknown;
};

type RenderAuthRedirectProps = {
  currentPath: string;
  debugInfo?: DebugStateInfo;
};

const MAX_REDIRECT_ATTEMPTS = 3;
const REDIRECT_ATTEMPT_COOKIE = "jbc_auth_redirect_attempt";
const REDIRECT_TIMEOUT_MS = 10000; // 10 seconds

// Helper to get cookie value
function getAuthAttemptCount(): number {
  if (typeof document === "undefined") return 0;
  const match = document.cookie.match(
    new RegExp(`(^| )${REDIRECT_ATTEMPT_COOKIE}=([^;]+)`),
  );
  return match ? parseInt(match[2], 10) || 0 : 0;
}

// Helper to set cookie value
function setAuthAttemptCount(count: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${REDIRECT_ATTEMPT_COOKIE}=${count}; path=/; max-age=300; SameSite=Lax`;
}

/**
 * Log authentication errors in a readable way
 */
function logAuthError(
  errorType: string,
  errorCode: AuthErrorCode | null,
  diagnosticInfo: BrowserDiagnosticInfo,
  debugInfo: DebugStateInfo = {},
  attemptCount: number = 0,
) {
  // Log multiple separate entries to ensure data isn't lost on redirect
  console.error(`🚨 [Auth] ${errorType} detected (${errorCode || "UNKNOWN"})`);
  console.error(
    `🚨 [Auth] Path: ${diagnosticInfo.windowLocation || "unknown"}`,
  );
  console.error(`🚨 [Auth] Attempt count: ${attemptCount}`);

  // Log browser details as plain text
  console.error(
    `🚨 [Auth] Browser: ${diagnosticInfo.userAgent?.substring(0, 100) || "unknown"} ` +
      `(Online: ${diagnosticInfo.onLine ? "Yes" : "No"}, ` +
      `Cookies: ${diagnosticInfo.cookiesEnabled ? "Enabled" : "Disabled"})`,
  );

  // Separately log important debug info
  if (debugInfo.currentPath) {
    console.error(`🚨 [Auth] Server path: ${debugInfo.currentPath}`);
  }

  if (debugInfo.error) {
    console.error(`🚨 [Auth] Server error: ${debugInfo.error}`);
  }

  // Log full objects
  try {
    console.error(
      "🚨 [Auth] Full diagnostic info:",
      JSON.parse(JSON.stringify(diagnosticInfo)),
    );
    console.error(
      "🚨 [Auth] Full debug info:",
      JSON.parse(JSON.stringify(debugInfo)),
    );
  } catch (err) {
    console.error("🚨 [Auth] Error stringifying debug objects:", err);
  }
}

export function RenderAuthRedirect({
  currentPath,
  debugInfo = {},
}: RenderAuthRedirectProps) {
  const [shouldRedirect, setShouldRedirect] = useState(true);
  const [redirectBlocked, setRedirectBlocked] = useState(false);
  const [redirectTimeout, setRedirectTimeout] = useState(false);
  const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
  const [errorAttemptCount, setErrorAttemptCount] = useState<number>(0);
  const [diagnosticInfo, setDiagnosticInfo] = useState<BrowserDiagnosticInfo>(
    {},
  );

  useEffect(() => {
    // Normalize incoming currentPath to exactly one leading slash
    const normalizedCurrentPath = (currentPath || "/").replace(/^\/+/, "/");
    // Store the time we started the redirect process
    const redirectStartTime = Date.now();

    // Capture basic browser info for diagnostics
    const browserInfo: BrowserDiagnosticInfo = {
      userAgent: navigator.userAgent,
      cookiesEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      referrer: document.referrer || "none",
      windowLocation: window.location.toString().split("?")[0],
    };

    setDiagnosticInfo(browserInfo);

    // Log mount / intent
    console.info(`[Auth] RenderAuthRedirect mounted for path: ${currentPath}`);

    // Client-side only check for redirect attempts
    const attemptCount = getAuthAttemptCount();
    console.debug(`[Auth] Current redirect attempt count: ${attemptCount}`);
    setErrorAttemptCount(attemptCount);

    // Set a timeout to detect stuck redirects
    const timeoutId = setTimeout(() => {
      console.error(
        `[Auth] Redirect timeout reached after ${Date.now() - redirectStartTime}ms, ` +
          `path: ${currentPath}`,
      );

      setRedirectTimeout(true);
      setErrorCode(AuthErrorCode.REDIRECT_TIMEOUT);
      browserInfo.errorCode = AuthErrorCode.REDIRECT_TIMEOUT;

      // Report the timeout to Sentry
      Sentry.captureMessage("Auth redirect timeout", {
        level: "error",
        tags: {
          redirectTimeout: "true",
          errorCode: AuthErrorCode.REDIRECT_TIMEOUT,
        },
        extra: {
          ...browserInfo,
          currentPath,
          attemptCount,
          timeElapsedMs: Date.now() - redirectStartTime,
          debugInfo,
        },
      });
    }, REDIRECT_TIMEOUT_MS);

    if (attemptCount >= MAX_REDIRECT_ATTEMPTS) {
      console.error(
        `[Auth] Redirect loop detected (${attemptCount} attempts), path: ${currentPath}`,
      );

      setErrorCode(AuthErrorCode.REDIRECT_LOOP);
      browserInfo.errorCode = AuthErrorCode.REDIRECT_LOOP;

      // Report to Sentry
      Sentry.captureException(new Error("Auth redirect loop detected"), {
        tags: {
          redirectLoop: "true",
          errorCode: AuthErrorCode.REDIRECT_LOOP,
        },
        extra: {
          ...browserInfo,
          currentPath,
          attemptCount,
          timestamp: new Date().toISOString(),
          debugInfo,
        },
        level: "error",
      });

      setShouldRedirect(false);
      setRedirectBlocked(true);
      clearTimeout(timeoutId);
      return;
    }

    // Increment attempt counter
    setAuthAttemptCount(attemptCount + 1);

    // Build redirect URL and log it before navigating using normalizedCurrentPath
    const redirectUrl = `/auth/login?returnTo=${encodeURIComponent(normalizedCurrentPath)}`;

    console.info(
      `[Auth] Performing redirect to: ${redirectUrl}, attempt: ${attemptCount + 1}`,
    );

    try {
      // Attempt the redirect
      window.location.href = redirectUrl;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(`[Auth] Redirect failed: ${errorMessage}`);

      setErrorCode(AuthErrorCode.REDIRECT_FAILED);
      browserInfo.errorCode = AuthErrorCode.REDIRECT_FAILED;

      Sentry.captureException(error, {
        tags: {
          redirectFailed: "true",
          errorCode: AuthErrorCode.REDIRECT_FAILED,
        },
        extra: { redirectUrl, browserInfo, debugInfo },
      });
      setRedirectBlocked(true);
      clearTimeout(timeoutId);
    }

    // Cleanup function to clear timeout
    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentPath, debugInfo]);

  // Early render while client-side effect runs
  if (typeof window === "undefined") {
    return null;
  }

  // If we've blocked the redirect due to too many attempts
  if (redirectBlocked || redirectTimeout) {
    const errorType = redirectTimeout
      ? "Redirect Timeout"
      : "Authentication Loop";

    // Log comprehensive error details
    logAuthError(
      errorType,
      errorCode,
      diagnosticInfo,
      debugInfo,
      errorAttemptCount,
    );

    return (
      <AuthErrorDisplay
        errorType={errorType}
        errorCode={errorCode || "UNKNOWN"}
        diagnosticInfo={diagnosticInfo}
        debugInfo={debugInfo}
        retryUrl="/auth/logout"
      />
    );
  }

  // Standard redirect state - visible before client-side JS executes the redirect
  // Ensure the UI fallback also uses a normalized path
  const normalizedForUI = (currentPath || "/").replace(/^\/+/, "/");
  const redirectUrl = `/auth/login?returnTo=${encodeURIComponent(normalizedForUI)}`;

  return shouldRedirect ? (
    <>
      <noscript>
        <meta httpEquiv="refresh" content={`0;url=${redirectUrl}`} />
        <div className="p-8">
          JavaScript is required for authentication. Please enable JavaScript
          and reload, or{" "}
          <a href={redirectUrl} className="text-sky-600 underline">
            click here
          </a>{" "}
          to continue.
        </div>
      </noscript>
      <div className="p-8 text-center">
        <p className="mt-2 mb-4">Redirecting to login...</p>
        <div className="mx-auto size-6 animate-spin rounded-full border-2 border-slate-300 border-t-sky-600"></div>
      </div>
    </>
  ) : null;
}
